import type { Incident } from '../../services/incidentsApi';
import {
  incidentReviewerAidSnapshot,
  incidentUsesReviewerAidPrimaryPolicy,
  isPreAnnotatedReviewerAidSnapshot,
} from './reviewerImagePolicy';

export type PreferredReviewAttachment = {
  filename: string;
  preferFileEndpoint: boolean;
  reviewRole?: string;
  snapshotOverlay?: string;
  preAnnotated?: boolean;
};

function parseAnyTs(value: unknown): number {
  const text = String(value || '').trim();
  if (!text) return 0;
  const parsed = Date.parse(text);
  return Number.isFinite(parsed) ? parsed : 0;
}

function refEventTs(value: any): number {
  const directMs = Number(
    value?.evidence_frame_target_ts_ms ??
    value?.threshold_crossed_ts_ms ??
    value?.event_ts_ms ??
    value?.evidence_ts_ms ??
    0,
  );
  if (Number.isFinite(directMs) && directMs > 0) return directMs;
  return parseAnyTs(value?.captured_at) || parseAnyTs(value?.uploaded_at);
}

function attachmentTs(attachment: any): number {
  const meta = attachment?.metadata || {};
  const extra = meta?.extra || {};
  const numericMs = (raw: unknown): number => {
    const value = Number(raw);
    return Number.isFinite(value) && value > 0 ? value : 0;
  };
  return (
    numericMs(meta?.snapshot_frame_ts_ms) ||
    numericMs(extra?.snapshot_frame_ts_ms) ||
    numericMs(meta?.evidence_ts_ms) ||
    numericMs(extra?.evidence_ts_ms) ||
    parseAnyTs(meta?.captured_at) ||
    parseAnyTs(meta?.timestamp) ||
    parseAnyTs(extra?.capture_completed_at) ||
    parseAnyTs(attachment?.uploaded_at)
  );
}

export function incidentEvidenceFileUri(incidentId: string, filename: string): string {
  return `/api/v1/incidents/${encodeURIComponent(incidentId)}/evidence/file/${encodeURIComponent(filename)}`;
}

/**
 * Package G / Detections still+clip picker.
 * Verify Scan must reuse this so overlayed T0 event_frame and preferred_clip
 * are not replaced by a thinner snapshot_uri / clip_uri-only path.
 */
export function selectPreferredReviewAttachment(
  incident: Incident | null | undefined,
  kind: 'snapshot' | 'video',
): PreferredReviewAttachment | null {
  if (!incident?.incident_id) return null;
  const attachments = (incident.evidence?.attachments || []) as any[];
  const matchingAttachments = Array.isArray(attachments)
    ? attachments.filter((attachment: any) =>
        attachment && attachment.filename &&
        (kind === 'snapshot'
          ? /\.(jpg|jpeg|png|gif)$/i.test(String(attachment.filename))
          : /\.(mp4|mov|mkv|webm)$/i.test(String(attachment.filename))),
      )
    : [];
  const eventTs =
    refEventTs(incident?.timing) ||
    refEventTs((incident as any)?.envelope_metadata?.timing) ||
    refEventTs((incident as any)?.metrics?.attributes?.timing) ||
    refEventTs(incident?.review_media?.source_truth?.[kind === 'snapshot' ? 'snapshot' : 'clip']) ||
    refEventTs(incident?.review_media?.primary_snapshot) ||
    parseAnyTs(incident?.created_at);

  const serverSelection = (incident as any)?.evidence_selection
    || (incident as any)?.evidence?.selection
    || null;
  const serverPreferred =
    kind === 'video'
      ? serverSelection?.preferred_clip
      : serverSelection?.preferred_snapshot;
  const serverFilename = String(serverPreferred?.filename || '').trim();
  if (serverFilename) {
    const match = matchingAttachments.find(
      (attachment: any) => String(attachment?.filename || '').trim() === serverFilename,
    );
    const meta = match?.metadata || {};
    const extra = meta?.extra || {};
    const reviewRole = String(
      serverPreferred?.capture_reason === 'reviewer_aid'
        ? 'reviewer_aid'
        : meta?.review_role || extra?.review_role || extra?.media_role || '',
    ).trim();
    const snapshotOverlay = String(
      meta?.snapshot_overlay || extra?.snapshot_overlay || extra?.snapshot_kind || '',
    ).trim();
    return {
      filename: serverFilename,
      preferFileEndpoint: true,
      reviewRole,
      snapshotOverlay,
      preAnnotated:
        reviewRole.toLowerCase() === 'reviewer_aid' || Boolean(snapshotOverlay),
    };
  }

  if (kind === 'snapshot' && matchingAttachments.length > 0 && eventTs > 0) {
    const sorted = matchingAttachments.slice().sort((a: any, b: any) => {
      const aDelta = Math.abs(attachmentTs(a) - eventTs);
      const bDelta = Math.abs(attachmentTs(b) - eventTs);
      if (aDelta !== bDelta) return aDelta - bDelta;
      const aExact = a?.metadata?.extra?.event_frame_exact_match === true ? 0 : 1;
      const bExact = b?.metadata?.extra?.event_frame_exact_match === true ? 0 : 1;
      if (aExact !== bExact) return aExact - bExact;
      const aTs = attachmentTs(a);
      const bTs = attachmentTs(b);
      return bTs - aTs;
    });
    const pick = sorted[0];
    if (pick?.filename) {
      const meta = pick?.metadata || {};
      const extra = meta?.extra || {};
      const reviewRole = String(
        meta?.review_role || extra?.review_role || extra?.media_role || '',
      ).trim();
      const snapshotOverlay = String(
        meta?.snapshot_overlay || extra?.snapshot_overlay || extra?.snapshot_kind || '',
      ).trim();
      return {
        filename: String(pick.filename),
        preferFileEndpoint: true,
        reviewRole,
        snapshotOverlay,
        preAnnotated:
          reviewRole.toLowerCase() === 'reviewer_aid' || Boolean(snapshotOverlay),
      };
    }
  }

  if (kind === 'snapshot' && incidentUsesReviewerAidPrimaryPolicy(incident)) {
    const reviewerAid = incidentReviewerAidSnapshot(incident);
    if (reviewerAid?.filename) {
      return {
        filename: String(reviewerAid.filename),
        preferFileEndpoint: true,
        reviewRole: String(reviewerAid.review_role || '').trim(),
        snapshotOverlay: String(reviewerAid.snapshot_overlay || '').trim(),
        preAnnotated: isPreAnnotatedReviewerAidSnapshot(reviewerAid),
      };
    }
  }
  const reviewMedia =
    kind === 'snapshot'
      ? [
          incident?.review_media?.source_truth?.snapshot,
          incident?.review_media?.unclassified?.snapshot,
          incident?.review_media?.reviewer_aids?.snapshot,
        ]
      : [
          incident?.review_media?.source_truth?.clip,
          incident?.review_media?.unclassified?.clip,
          incident?.review_media?.reviewer_aids?.clip,
        ];
  const preferredRef = reviewMedia.find((value: any) => String(value?.filename || '').trim());
  const preferredFilename = String(preferredRef?.filename || '').trim();
  if (preferredFilename) {
    const reviewRole = String(preferredRef?.review_role || '').trim();
    const snapshotOverlay = String(preferredRef?.snapshot_overlay || '').trim();
    return {
      filename: preferredFilename,
      preferFileEndpoint: true,
      reviewRole,
      snapshotOverlay,
      preAnnotated: reviewRole.toLowerCase() === 'reviewer_aid' || Boolean(snapshotOverlay),
    };
  }

  const latestFilename =
    kind === 'snapshot'
      ? String((incident.evidence as any)?.latest?.snapshot?.filename || '').trim()
      : String((incident.evidence as any)?.latest?.video?.filename || '').trim();

  if (matchingAttachments.length > 0) {
    const sorted = matchingAttachments.slice().sort((a: any, b: any) => {
      if (kind === 'video' && eventTs > 0) {
        const windowCover = (att: any): number => {
          const meta = att?.metadata || {};
          const extra = meta?.extra || {};
          const start = Number(meta?.clip_start_ts_ms || extra?.clip_start_ts_ms || 0);
          const end = Number(meta?.clip_end_ts_ms || extra?.clip_end_ts_ms || 0);
          const grace = Number(meta?.clip_event_grace_ms || extra?.clip_event_grace_ms || 750);
          if (start > 0 && end > 0) {
            return eventTs >= start - grace && eventTs <= end + grace ? 0 : 1;
          }
          return 2;
        };
        const aCover = windowCover(a);
        const bCover = windowCover(b);
        if (aCover !== bCover) return aCover - bCover;
      }
      if (eventTs > 0) {
        const aDelta = Math.abs(attachmentTs(a) - eventTs);
        const bDelta = Math.abs(attachmentTs(b) - eventTs);
        if (aDelta !== bDelta) return aDelta - bDelta;
      }
      const aTs = attachmentTs(a);
      const bTs = attachmentTs(b);
      return aTs - bTs;
    });
    const pick = sorted[0];
    if (pick?.filename) {
      const meta = pick?.metadata || {};
      const extra = meta?.extra || {};
      const reviewRole = String(
        meta?.review_role || extra?.review_role || extra?.media_role || '',
      ).trim();
      const snapshotOverlay = String(
        meta?.snapshot_overlay || extra?.snapshot_overlay || extra?.snapshot_kind || '',
      ).trim();
      return {
        filename: String(pick.filename),
        preferFileEndpoint: true,
        reviewRole,
        snapshotOverlay,
        preAnnotated:
          reviewRole.toLowerCase() === 'reviewer_aid' || Boolean(snapshotOverlay),
      };
    }
  }

  if (latestFilename) return { filename: latestFilename, preferFileEndpoint: false };
  return null;
}

export function resolvePreferredReviewMediaUri(
  incident: Incident | null | undefined,
  kind: 'snapshot' | 'video',
): string {
  const incidentId = String(incident?.incident_id || '').trim();
  const pick = selectPreferredReviewAttachment(incident, kind);
  if (pick?.filename && incidentId && pick.preferFileEndpoint) {
    return incidentEvidenceFileUri(incidentId, pick.filename);
  }
  if (kind === 'video') {
    const clipUri = String((incident as any)?.clip_uri || '').trim();
    if (clipUri) return clipUri;
  } else {
    const stillUri = String(
      (incident as any)?.preferred_snapshot?.uri
      || (incident as any)?.snapshot_uri
      || (incident as any)?.primary_snapshot?.uri
      || '',
    ).trim();
    if (stillUri) return stillUri;
  }
  if (pick?.filename && incidentId) {
    return incidentEvidenceFileUri(incidentId, pick.filename);
  }
  return '';
}
